import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import {
    DataTableCustomized,
    createDateFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import EyeIcons from '@/icons/EyeIcons'
import { PreviewServiceNotificationModalProps } from '@/modals/ServiceModals/PreviewServiceNotification'
import { deleteServiceNotification } from '@/modules/Service/services'
import {
    ServiceCustomerMailer,
    ServiceNotification,
} from '@/modules/Service/types'
import { Mail, Plus, Server } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton } from 'vnetwork-ui'

interface ServiceNotificationListProps {
    serviceID: number
    notifications: ServiceNotification[]
    mailers: ServiceCustomerMailer[]
    onEdit?: (notification: ServiceNotification) => void
    onSendEmail?: (notificationId: number) => void
    onCreate?: () => void
    onDelivery?: () => void
    isDrawerOpen?: boolean
    isDeliveryOpen?: boolean
}

const List: React.FC<ServiceNotificationListProps> = ({
    serviceID,
    notifications = [],
    mailers = [],
    onEdit,
    onSendEmail,
    onCreate,
    onDelivery,
    isDrawerOpen,
    isDeliveryOpen,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const { mutateAsync: deleteMutation } = useMutationWithHandler(
        {
            mutationFn: deleteServiceNotification,
        },
        {
            successMessage: t('delete-service-notification-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_NOTIFICATIONS,
        }
    )

    const handleDelete = (notification: ServiceNotification) => {
        setConfirmation({
            open: true,
            title: t('delete-service-notification'),
            description: t('delete-service-notification-confirm', {
                name: notification.notification_name,
            }),
            onConfirm: async () => {
                await deleteMutation({
                    serviceID,
                    notificationID: notification.service_system_information_id,
                })
            },
        })
    }

    const handlePreview = (notification: ServiceNotification) => {
        const mailerID = mailers?.[0]?.customer_mailer_id
        if (!mailerID) return
        setModal<PreviewServiceNotificationModalProps>(
            'ServiceModals/PreviewServiceNotification/index',
            {
                serviceID,
                notificationID: notification.service_system_information_id,
                mailerID,
            }
        )
    }

    const columns = useMemo(
        (): DataTableColumn<ServiceNotification>[] => [
            {
                key: 'notification_name',
                title: t('notification_name'),
                width: 250,
            },
            {
                key: 'dns_enable',
                title: 'DNS',
                width: 80,
                valueGetter: (row) => (row.dns_enable ? t('yes') : t('no')),
            },
            {
                key: 'metadata' as any,
                title: t('metadata'),
                width: 100,
                valueGetter: (row) => row.metadata?.length || 0,
            },
            {
                key: 'documentations' as any,
                title: t('documentation'),
                width: 100,
                valueGetter: (row) => row.documentations?.length || 0,
            },
            {
                key: 'attachments' as any,
                title: t('attachments'),
                width: 100,
                valueGetter: (row) => row.attachments?.length || 0,
            },
            {
                key: 'created_at',
                title: t('created_at'),
                width: 150,
                formatter: createDateFormatter('HH:mm DD/MM/YYYY'),
            },
            {
                key: 'actions',
                title: '',
                width: 120,
                render: (_value, row) => (
                    <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {mailers.length > 0 && (
                            <IconButton
                                size="small"
                                onClick={() => handlePreview(row)}
                            >
                                <EyeIcons className="h-4 w-4 text-primary" />
                            </IconButton>
                        )}
                        <IconButton size="small" onClick={() => onEdit?.(row)}>
                            <EditIcons className="h-4 w-4 text-primary" />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(row)}
                        >
                            <DeleteOutlineIcons className="h-4 w-4" />
                        </IconButton>
                    </div>
                ),
            },
        ],
        [t, mailers]
    )

    return (
        <Paper className="mt-2">
            <div className="flex items-center justify-between mb-3">
                <p className="flex items-center gap-2 font-medium text-primary">
                    <Server className="h-4 w-4" />
                    {t('service-notifications')}
                </p>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={onCreate}
                        disabled={isDrawerOpen}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        {t('create-notification-mailer')}
                    </Button>
                    <Button
                        size="sm"
                        onClick={onDelivery}
                        disabled={isDeliveryOpen}
                    >
                        <Mail className="h-4 w-4 mr-1" />
                        {t('delivery-notification-mailer')}
                    </Button>
                </div>
            </div>
            <DataTableCustomized<ServiceNotification>
                columns={columns}
                rows={notifications}
                rowKey={(row) => String(row.service_system_information_id)}
                onRowClick={(row) =>
                    onSendEmail?.(row.service_system_information_id)
                }
                emptyMessage={t('no-data')}
                bordered
                size="small"
                pageSize={5}
            />
        </Paper>
    )
}

export default List
